import React, { useRef, useState } from 'react';
import { Modal, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useThemedStyles } from '../hooks/useThemedStyles';
import type { ContactIdentifierOption } from '../hooks/useContactConversationPane';

interface ConversationIdentifierDropdownProps {
  options: ContactIdentifierOption[];
  selectedChannel?: string | null;
  selectedValue?: string | null;
  formatChannelName: (channel: string) => string;
  onSelect: (option: ContactIdentifierOption) => void;
}

const normalize = (value?: string | null) => (value || '').replace(/^\+/, '').trim();

const getIdentifierIcon = (channel: string): keyof typeof Ionicons.glyphMap => {
  switch (channel) {
    case 'phone':
      return 'call-outline';
    case 'whatsapp':
      return 'logo-whatsapp';
    case 'facebook-messenger':
      return 'logo-facebook';
    case 'instagram':
      return 'logo-instagram';
    case 'telegram':
      return 'paper-plane';
    case 'x-twitter':
      return 'logo-twitter';
    default:
      return 'chatbubble-ellipses-outline';
  }
};

export const ConversationIdentifierDropdown: React.FC<ConversationIdentifierDropdownProps> = ({
  options,
  selectedChannel,
  selectedValue,
  formatChannelName,
  onSelect,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { width: windowWidth } = useWindowDimensions();
  const triggerRef = useRef<View>(null);
  const [open, setOpen] = useState(false);
  // Trigger's on-screen rect — the menu opens directly under it (aligned to
  // its start edge in LTR, end edge in RTL) instead of a fixed screen corner,
  // so it stays attached to the header at any pane width or direction.
  const [anchor, setAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const selected = options.find((option) =>
    option.channel === selectedChannel &&
    normalize(option.value) === normalize(selectedValue)
  ) || options[0];

  if (!selected) {
    return null;
  }

  const openMenu = () => {
    if (options.length <= 1) return;
    const node = triggerRef.current as any;
    if (node?.measureInWindow) {
      node.measureInWindow((x: number, y: number, width: number, height: number) => {
        setAnchor({ x, y, width, height });
        setOpen(true);
      });
    } else {
      setAnchor(null);
      setOpen(true);
    }
  };

  const EDGE = 8;
  const menuWidth = Math.min(Math.max(anchor?.width ?? 0, 260), windowWidth - EDGE * 2);
  const menuLeft = anchor
    ? Math.min(
        Math.max(EDGE, rtl.isRTL ? anchor.x + anchor.width - menuWidth : anchor.x),
        windowWidth - menuWidth - EDGE
      )
    : EDGE;
  const menuPosition = {
    top: anchor ? anchor.y + anchor.height + 4 : 92,
    left: menuLeft,
    width: menuWidth,
  };

  const selectOption = (option: ContactIdentifierOption) => {
    setOpen(false);
    onSelect(option);
  };

  return (
    <>
      <TouchableOpacity
        ref={triggerRef as any}
        style={[styles.trigger, { flexDirection: rtl.rowDirection }]}
        onPress={openMenu}
        activeOpacity={options.length > 1 ? 0.75 : 1}
      >
        <View style={styles.triggerIcon}>
          <Ionicons name={getIdentifierIcon(selected.channel)} size={15} color={colors.primary as string} />
        </View>
        <View style={styles.triggerTextWrap}>
          <Text style={[styles.triggerTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
            {formatChannelName(selected.channel)}
          </Text>
          <Text style={[styles.triggerValue, { textAlign: rtl.textStart }]} numberOfLines={1}>
            {selected.value}
          </Text>
        </View>
        {options.length > 1 && (
          <Ionicons name="chevron-down" size={16} color={colors.text.secondary as string} />
        )}
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setOpen(false)}>
          <View style={[styles.menu, menuPosition]}>
            {options.map((option) => {
              const isSelected = option.channel === selected.channel && normalize(option.value) === normalize(selected.value);
              return (
                <TouchableOpacity
                  key={option.key}
                  style={[styles.option, { flexDirection: rtl.rowDirection }, isSelected && styles.optionSelected]}
                  onPress={() => selectOption(option)}
                  activeOpacity={0.75}
                >
                  <View style={styles.optionIcon}>
                    <Ionicons name={getIdentifierIcon(option.channel)} size={16} color={colors.primary as string} />
                  </View>
                  <View style={styles.optionTextWrap}>
                    <Text style={[styles.optionTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
                      {formatChannelName(option.channel)}
                    </Text>
                    <Text style={[styles.optionValue, { textAlign: rtl.textStart }]} numberOfLines={1}>
                      {option.value}
                    </Text>
                  </View>
                  {isSelected && <Ionicons name="checkmark" size={18} color={colors.primary as string} />}
                </TouchableOpacity>
              );
            })}
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  trigger: {
    alignItems: 'center',
    minWidth: 0,
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    backgroundColor: colors.background.secondary as string,
  },
  triggerTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  triggerIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${String(colors.primary)}12`,
  },
  triggerTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  triggerValue: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.12)',
  },
  menu: {
    position: 'absolute',
    borderRadius: 8,
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  option: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  optionSelected: {
    backgroundColor: colors.background.secondary as string,
  },
  optionIcon: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `${String(colors.primary)}12`,
  },
  optionTextWrap: {
    flex: 1,
    minWidth: 0,
  },
  optionTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  optionValue: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
});
